import { Alert, Button, Stack, TextField } from '@mui/material'
import { useState, type FormEvent, type ReactNode } from 'react'

import { api, ApiError } from '../api/client'

/** Mirrors PASSWORD_MIN_LENGTH; the server is what actually enforces it. */
export const MIN_LENGTH = 12

interface Props {
  submitLabel: string
  busyLabel: string
  /** Called after the server accepts the change. */
  onDone: () => void | Promise<void>
  /** Rendered under the submit button, e.g. a "sign out instead" escape. */
  children?: ReactNode
}

/**
 * The three-field password change, shared by the forced-change screen and
 * My account.
 *
 * One copy so the two cannot drift into disagreeing about what a valid password
 * is -- the failure mode being a screen that accepts something the other
 * refuses, or worse, one that quietly asks for less.
 */
export default function ChangePasswordForm({ submitLabel, busyLabel, onDone, children }: Props) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const mismatch = confirm.length > 0 && next !== confirm
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const ready = Boolean(current) && next.length >= MIN_LENGTH && next === confirm

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.changePassword(current, next)
      await onDone()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not change the password.')
      setBusy(false)
    }
  }

  return (
    <Stack spacing={3} component="form" onSubmit={(event) => void submit(event)}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        label="Current password"
        type="password"
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        autoComplete="current-password"
        required
        fullWidth
      />
      <TextField
        label="New password"
        type="password"
        value={next}
        onChange={(event) => setNext(event.target.value)}
        autoComplete="new-password"
        required
        fullWidth
        error={tooShort}
        helperText={`At least ${MIN_LENGTH} characters.`}
      />
      <TextField
        label="Repeat new password"
        type="password"
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
        autoComplete="new-password"
        required
        fullWidth
        error={mismatch}
        helperText={mismatch ? 'These do not match.' : ' '}
      />

      <Button type="submit" variant="contained" size="large" disabled={busy || !ready}>
        {busy ? busyLabel : submitLabel}
      </Button>
      {children}
    </Stack>
  )
}
